export function money(n) {
  return '$' + Number(n || 0).toLocaleString('es-AR');
}

// Nombre para mostrar, de-duplicando: si el apellido ya está incluido en el
// nombre (o ambos campos son iguales), no lo repite. Cubre filas donde se cargó
// el nombre completo en ambos campos (ej. ventas online viejas).
export function fullName(name, lastname) {
  const n = String(name || '').trim();
  const l = String(lastname || '').trim();
  if (!l || n === l) return n;
  if (n.toLowerCase().endsWith(' ' + l.toLowerCase())) return n;
  return `${n} ${l}`.trim();
}

// Zona horaria del boliche. Se fija explícitamente y NO se toma la del dispositivo:
// si alguien mira el panel desde afuera del país, o desde un celular con la zona mal
// configurada, las horas de la noche tienen que seguir siendo las del boliche.
export const TZ_AR = 'America/Argentina/Buenos_Aires';

// Fecha + hora de un timestamp real (instante), en hora del boliche.
// Para la columna "fecha/hora de venta" (tickets.created_at).
export function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: TZ_AR });
  } catch { return '—'; }
}

export function fmtDate(iso) {
  if (!iso) return '—';
  // Fechas "solas" (columnas DATE): tomamos año/mes/día tal cual y construimos
  // una fecha LOCAL, sin conversión de zona horaria. Así no se corre un día
  // aunque el backend la mande como medianoche UTC (ej "2026-06-27T00:00:00Z").
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '—';
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
}

export function isPast(ev) {
  if (ev.status === 'pasada') return true;
  if (!ev.date) return false;
  // Misma lógica timezone-safe que fmtDate para no adelantar/atrasar un día.
  const dia = String(ev.date).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dia)) return false;
  // "Hoy" es el día del BOLICHE, no el del dispositivo: si no, un evento de esta noche
  // pasaba a "finalizado" a las 21:00 para quien tuviera el celular en otra zona.
  const hoy = new Date().toLocaleDateString('en-CA', { timeZone: TZ_AR });
  return dia < hoy;
}

export function originBase() {
  return typeof window !== 'undefined' ? window.location.origin : '';
}

// Redimensiona un logo conservando la transparencia (exporta PNG, sin fondo).
// Ideal para isotipos PNG transparentes (navbar, tickets, emails).
export function resizeLogo(file, maxWidth = 400) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) { reject(new Error('El archivo no es una imagen')); return; }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('No se pudo procesar la imagen'));
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, w, h); // sin fondo: preserva transparencia
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/png'));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

// Comprime una imagen en el navegador: máx 1200px de ancho, JPEG calidad 0.8.
// Devuelve una promesa con el data URL resultante (mantiene liviana la base).
// Texto para compartir un link de venta (WhatsApp): primero el evento, luego la cantidad + el
// lote, y la aclaración de que cada asistente completa sus datos y presenta el QR en la puerta.
export function shareLinkText({ eventName, eventDate, cantidad, lote }) {
  const cant = Number(cantidad) || 1;
  const l = (lote || 'entrada').trim();
  let fecha = '';
  if (eventDate) { const d = new Date(String(eventDate).slice(0, 10) + 'T00:00:00'); if (!isNaN(d)) fecha = ` · ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`; }
  return `${eventName || 'Evento'}${fecha}\nLink válido para ${cant} ${l}.\nCada asistente deberá completar sus datos personales y presentar el QR en la puerta junto a su documento de identidad.`;
}

// bg: color de relleno detrás de la imagen. Los flyers van sobre NEGRO (el sitio es
// oscuro), pero un documento escaneado o una captura con transparencia sobre negro
// puede quedar ilegible si el texto es oscuro — para esos casos se pasa '#fff'.
export function resizeImage(file, maxWidth = 1200, quality = 0.8, bg = '#000') {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('El archivo no es una imagen'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('No se pudo procesar la imagen'));
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

// Recorta la imagen a CUADRADA tomando el centro, y después la achica. Es para las
// fotos de premios: se sacan con el celular en vertical o en horizontal, y si cada
// una conserva su forma la grilla queda despareja. Recortando al subir, lo que se
// guarda ya es cuadrado y todas las pantallas la muestran igual sin hacer cuentas.
//
// Se recorta el CENTRO, que es donde la gente pone el objeto al sacarle la foto.
export function squareImage(file, size = 800, quality = 0.8, bg = '#fff') {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('El archivo no es una imagen'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('No se pudo procesar la imagen'));
      img.onload = () => {
        const lado = Math.min(img.width, img.height);         // el cuadrado más grande que entra
        const sx = Math.round((img.width - lado) / 2);
        const sy = Math.round((img.height - lado) / 2);
        // Nunca agrandar: si la foto es más chica que `size`, se deja como está.
        const out = Math.min(size, lado);
        const canvas = document.createElement('canvas');
        canvas.width = out;
        canvas.height = out;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, out, out);
        ctx.drawImage(img, sx, sy, lado, lado, 0, 0, out, out);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

// Texto del botón de acceso de la web pública: ACCEDER si no hay sesión, MI CUENTA
// si ya entró. Cuenta como sesión tanto la del comprador (por boliche) como la del
// equipo, porque desde ese mismo botón entran los dos.
export function accesoLabel(slug) {
  try {
    if (localStorage.getItem('tk_token')) return 'Mi cuenta';           // equipo
    if (slug && localStorage.getItem(`tk_buyer_${slug}`)) return 'Mi cuenta'; // comprador
  } catch { /* modo privado o storage bloqueado */ }
  return 'Acceder';
}

// Lo que vale un link de RRPP: lo que el RRPP cobró y sobre lo que rinde. ESPEJO EXACTO
// de linkValueSql() del backend — si uno cambia y el otro no, el panel le muestra al
// RRPP una deuda y la rendición del servidor le reclama otra.
//
// Eso pasó con la promo: cuando empezó a descontar de verdad se actualizó el backend y
// esto quedó igual. Un link de 4 a $11.000 con 4x3 se veía acá en $44.000 (a rendir
// $39.600) mientras el servidor pedía $29.700.
//
//   · El cupón descuenta UNA sola entrada del link (es de la persona que lo ganó, no
//     del grupo), y esa diferencia la absorbe el boliche.
//   · La promo se lee de `promo_discount`, congelado al generar el link: apagar la promo
//     al otro día no puede cambiar lo que el RRPP ya cobró.
export function linkValue(link) {
  const price = Number(link?.lot_price || 0);
  const uses = Number(link?.max_uses || 0);
  const disc = !link?.coupon_code ? 0
    : (link.coupon_type === 'fixed'
        ? Math.min(price, Number(link.coupon_value) || 0)
        : Math.round(price * (Number(link.coupon_value) || 0) / 100));
  const promo = Number(link?.promo_discount) || 0;
  return Math.max(0, uses * price - disc - promo);
}

// "APELLIDO, Nombre" para listas donde se busca a una persona: en una lista de 1000
// asistentes se busca por apellido, así que va primero. Si no hay apellido cargado,
// devuelve el nombre solo (sin la coma colgando).
//
// Contempla el caso de siempre: que alguien haya escrito el apellido DENTRO del nombre
// ("Juan Perez" + apellido "Perez"). Ahí se quita del nombre para no repetirlo.
export function apellidoNombre(name, lastname) {
  const n = String(name || '').trim();
  const l = String(lastname || '').trim();
  if (!l) return n;
  if (n.toLowerCase() === l.toLowerCase()) return l;
  const soloNombre = n.toLowerCase().endsWith(' ' + l.toLowerCase())
    ? n.slice(0, n.length - l.length).trim()
    : n;
  return soloNombre ? `${l}, ${soloNombre}` : l;
}

// Los tres estados del traslado de un lote, guardados en DOS booleanos:
//   sin traslado → has_transport false
//   combo        → has_transport true,  solo_traslado false  (entrada + micro, dos QR)
//   solo micro   → has_transport true,  solo_traslado true   (pasaje suelto, no es persona)
// Vive acá porque lo usan los DOS formularios de lotes —el del evento y el de
// plantillas— y ya nos pasó de tocar uno y olvidarnos del otro.
export function modoTraslado(f) {
  if (f.solo_traslado) return 'solo';
  return f.has_transport ? 'combo' : 'no';
}

export function setModoTraslado(set, modo) {
  set('has_transport', modo !== 'no');
  set('solo_traslado', modo === 'solo');
}

// Los tres tipos de lote, para el filtro del selector. Con ingreso general, VIP, los
// combos de 6 localidades y los traslados sueltos de otras 6, la lista se vuelve
// imposible de recorrer: el filtro la parte en tres.
export function tipoLote(l) {
  if (l?.solo_traslado) return 'traslado';
  return l?.has_transport ? 'combo' : 'ingreso';
}

export const TIPOS_LOTE = [
  ['ingreso', 'Ingresos'],
  ['combo', 'Combos'],
  ['traslado', 'Traslados'],
];
